// Stage 2 "Dashboard" · 2.3 (a) — the five tiles. Every number is a link; the assembly / ready-to-lock / PM tiles open their
// list under the tiles (each row a link that selects the engagement and opens its page).
import React, { useState } from "react";
import { asArray } from "@/lib/asArray";
import { fmtDate } from "@/lib/dt";
import { fmtNumber } from "@/lib/format";
import { FD, TILE_ACCENT, STAGES, STAGE_CHIP, TARGET, fill } from "./fdTheme";
import { FdLink, Chip } from "./FdPrimitives";

function Tile({ id, label, hint, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg px-4 py-3 shadow-sm"
         style={{ background: FD.panel, border: `1px solid ${FD.rowLine}`, borderTop: `4px solid ${TILE_ACCENT[id]}` }}>
      <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: FD.thText }}>{label}</div>
      {hint ? <div className="text-[11px]" style={{ color: FD.muted }}>{hint}</div> : null}
      <div className="mt-2">{children}</div>
    </div>
  );
}

function Big({ children, onClick, testid, pressed }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-expanded={pressed}
            className="text-3xl font-bold tabular-nums hover:underline" style={{ color: FD.link }}>
      {children}
    </button>
  );
}

export default function FdTiles({ tiles, t, lang }) {
  const [open, setOpen] = useState(null);   // "assembly" | "ready" | "pm" | null
  const toggle = (k) => setOpen((o) => (o === k ? null : k));
  const by = (tiles && tiles.by_stage) || {};
  const assembly = (tiles && tiles.assembly_due_30) || [];
  const ready = (tiles && tiles.ready_to_lock) || { ready: 0, total: 0, items: [] };
  const pm = (tiles && tiles.uncorrected_at_or_over_pm) || [];
  const rp = (tiles && tiles.open_review_points) || 0;
  const rpTotal = tiles ? tiles.review_points_total : undefined;   // D-1: "n open of N" (N = every live review point)
  const n = (v) => fmtNumber(v || 0, lang);

  return (
    <div data-testid="fd-tiles">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <Tile id="stages" label={t("fd.tiles.stages")} testid="fd-tile-stages">
          <div className="flex flex-wrap gap-1.5">
            {STAGES.map((s) => (
              <FdLink key={s} to={TARGET.engagementsByStage(s)} testid={`fd-tile-stage-${s}`} className="!inline-block">
                <Chip bg={STAGE_CHIP[s].bg} fg={STAGE_CHIP[s].fg}>{t(`fd.stage.${s}`)} <b className="tabular-nums">{n(by[s])}</b></Chip>
              </FdLink>
            ))}
          </div>
        </Tile>
        <Tile id="assembly" label={t("fd.tiles.assembly")} hint={t("fd.tiles.assemblyHint")} testid="fd-tile-assembly">
          <Big onClick={() => toggle("assembly")} testid="fd-tile-assembly-count" pressed={open === "assembly"}>{n(assembly.length)}</Big>
          {assembly.some((a) => a.overdue) ? <div className="text-xs font-semibold" style={{ color: FD.red }}>{t("fd.tiles.overdue")}: {n(assembly.filter((a) => a.overdue).length)}</div> : null}
        </Tile>
        <Tile id="ready" label={t("fd.tiles.ready")} testid="fd-tile-ready">
          <Big onClick={() => toggle("ready")} testid="fd-tile-ready-count" pressed={open === "ready"}>{fill(t("fd.tiles.readyOf"), { n: n(ready.ready), total: n(ready.total) })}</Big>
        </Tile>
        <Tile id="review" label={t("fd.tiles.review")} testid="fd-tile-review">
          <FdLink to={TARGET.reviewPoints} testid="fd-tile-review-count" className="text-2xl font-bold tabular-nums">
            {typeof rpTotal === "number" ? fill(t("fd.tiles.rpOpenOf"), { n: n(rp), total: n(rpTotal) }) : n(rp)}
          </FdLink>
        </Tile>
        <Tile id="pm" label={t("fd.tiles.pm")} testid="fd-tile-pm">
          <Big onClick={() => toggle("pm")} testid="fd-tile-pm-count" pressed={open === "pm"}>{n(pm.length)}</Big>
        </Tile>
      </div>

      {open === "assembly" && (
        <TileList testid="fd-list-assembly" title={t("fd.tiles.assembly")} empty={t("fd.tiles.none")} rows={assembly.map((a) => ({
          key: a.engagement_id, to: TARGET.fileAssembly, engId: a.engagement_id, color: a.overdue ? FD.red : undefined,
          text: `${a.client_name} — ${a.overdue ? t("fd.tiles.overdue") : t("fd.tiles.due")} ${fmtDate(a.assembly_due)}`,
        }))} />
      )}
      {open === "ready" && (
        <TileList testid="fd-list-ready" title={t("fd.tiles.ready")} empty={t("fd.tiles.none")} rows={asArray(ready.items).map((a) => ({
          key: a.engagement_id, to: TARGET.fileAssembly, engId: a.engagement_id, text: a.client_name,
        }))} />
      )}
      {open === "pm" && (
        <TileList testid="fd-list-pm" title={t("fd.tiles.pm")} empty={t("fd.tiles.none")} rows={pm.map((a) => ({
          key: a.engagement_id, to: TARGET.w9, engId: a.engagement_id, color: FD.red,
          text: `${a.client_name} — ${fill(t("fd.ms.ofPm"), { pct: fmtNumber(a.pct, lang, { maximumFractionDigits: 0 }) })}`,
        }))} />
      )}
    </div>
  );
}

function TileList({ testid, title, rows, empty }) {
  return (
    <div data-testid={testid} className="mt-3 rounded-lg px-4 py-2 shadow-sm" style={{ background: FD.panel, border: `1px solid ${FD.rowLine}` }}>
      <div className="text-xs font-semibold uppercase" style={{ color: FD.thText }}>{title}</div>
      {rows.length === 0 ? <div className="py-2 text-sm" style={{ color: FD.muted }}>{empty}</div> : (
        <ul className="divide-y" style={{ borderColor: FD.rowLine }}>
          {rows.map((r) => (
            <li key={r.key} className="py-1.5 text-sm">
              <FdLink to={r.to} engId={r.engId} testid={`${testid}-${r.key}`} style={r.color ? { color: r.color } : undefined}>{r.text}</FdLink>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
